/**
 * SignalList - Display list of deterministic signals
 */

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Calendar,
  Database,
  Target,
} from 'lucide-react';
import type { Signal, SignalType, SignalSeverity } from './types';

interface SignalListProps {
  signals: Signal[];
  selectedPeriod: string | null;
  onPeriodSelect: (period: string | null) => void;
}

const signalTypeIcons: Record<SignalType, React.ReactNode> = {
  SPIKE_DROP: <TrendingUp className="h-4 w-4" />,
  TREND_BREAK: <TrendingDown className="h-4 w-4" />,
  THRESHOLD_HIT: <Target className="h-4 w-4" />,
  NEAR_CLOSE: <Calendar className="h-4 w-4" />,
  DATA_QUALITY: <Database className="h-4 w-4" />,
};

const signalTypeLabels: Record<SignalType, string> = {
  SPIKE_DROP: 'Stor förändring',
  TREND_BREAK: 'Trendomslag',
  THRESHOLD_HIT: 'Väsentlighetströskel',
  NEAR_CLOSE: 'Bokslutsmånad',
  DATA_QUALITY: 'Datakvalitet',
};

const severityColors: Record<SignalSeverity, string> = {
  HIGH: 'bg-destructive/10 text-destructive border-destructive/30',
  MEDIUM: 'bg-warning/10 text-warning-foreground border-warning/30',
  LOW: 'bg-muted text-muted-foreground border-muted-foreground/20',
};

const severityBadgeVariants: Record<SignalSeverity, 'destructive' | 'secondary' | 'outline'> = {
  HIGH: 'destructive',
  MEDIUM: 'secondary',
  LOW: 'outline',
};

export function SignalList({ signals, selectedPeriod, onPeriodSelect }: SignalListProps) {
  // Filter by selected period if set
  const filteredSignals = selectedPeriod
    ? signals.filter(s => s.period === selectedPeriod)
    : signals;
  
  // Sort by severity (HIGH first), then by period
  const sortedSignals = [...filteredSignals].sort((a, b) => {
    const severityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return a.period.localeCompare(b.period);
  });
  
  if (sortedSignals.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">
          {selectedPeriod 
            ? `Inga signaler för ${selectedPeriod}` 
            : 'Inga signaler hittades'
          }
        </p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {sortedSignals.map((signal) => (
          <div
            key={signal.id}
            className={`
              p-3 rounded-lg border cursor-pointer transition-all
              ${severityColors[signal.severity]}
              ${signal.period === selectedPeriod ? 'ring-2 ring-primary' : ''}
              hover:opacity-80
            `}
            onClick={() => onPeriodSelect(signal.period === selectedPeriod ? null : signal.period)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {signalTypeIcons[signal.signal_type]}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {signalTypeLabels[signal.signal_type]}
                    </span>
                    <Badge variant={severityBadgeVariants[signal.severity]} className="text-xs">
                      {signal.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {signal.period}
                  </p>
                </div>
              </div>
              {signal.change_value !== undefined && (
                <span className={`text-sm font-medium whitespace-nowrap ${
                  signal.change_value > 0 ? 'text-success' : 'text-destructive'
                }`}>
                  {signal.change_value > 0 ? '+' : ''}{(signal.change_value * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <p className="text-xs mt-2 opacity-90">
              {signal.explanation}
            </p>
            {signal.is_yoy && (
              <Badge variant="outline" className="text-xs mt-2">
                Jämförelse mot föregående år
              </Badge>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

/**
 * Compact signal summary for header display
 */
export function SignalSummary({ signals }: { signals: Signal[] }) {
  const counts = {
    HIGH: signals.filter(s => s.severity === 'HIGH').length,
    MEDIUM: signals.filter(s => s.severity === 'MEDIUM').length,
    LOW: signals.filter(s => s.severity === 'LOW').length,
  };
  
  return (
    <div className="flex items-center gap-2">
      {counts.HIGH > 0 && (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          {counts.HIGH}
        </Badge>
      )}
      {counts.MEDIUM > 0 && (
        <Badge variant="secondary" className="gap-1">
          {counts.MEDIUM}
        </Badge>
      )}
      {counts.LOW > 0 && (
        <Badge variant="outline" className="gap-1">
          {counts.LOW}
        </Badge>
      )}
    </div>
  );
}
